import { EditOutlined } from '@ant-design/icons'
import {
  Card,
  Progress,
  Table,
  Tag,
  Typography,
} from '@surecompanies/core_components'

import { Select, Space, Switch } from 'antd'
import React, { useCallback } from 'react'
import { browserHistory } from 'react-router'
import styles from '../CustomerOnboarding.module.scss'
import useCustomerOnboardingList, {
  CustomerOnboardingRow,
} from '../hooks/useCustomerOnboardingList'
import { TEMPLATE_TYPE_LABELS } from '../models/templateType'
import { CENSUS_ONBOARDING } from '../paths'

export default function AllCustomersTable() {
  const {
    data,
    total,
    isLoading,
    options,
    selectedClientPublicKey,
    page,
    pageSize,
    includeTestCompanies,
    handleToggleTestCompanies,
    handleSelectClient,
    handlePageChange,
  } = useCustomerOnboardingList()

  const navigateToCustomer = useCallback((id: string) => {
    browserHistory.push(`/${CENSUS_ONBOARDING}/${id}`)
  }, [])

  const columns = [
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: 'Onboarding Progress',
      key: 'progress',
      render: (_: unknown, row: CustomerOnboardingRow) => {
        const hasAnyHrisActive =
          (row.activeHrisMappings?.length ?? 0) > 0 ||
          row.activeHrisMappingVersion != null
        const steps = [
          hasAnyHrisActive,
          row.activeEligibleMappingVersion != null,
        ]
        const completed = steps.filter(Boolean).length
        const total = steps.length
        const percent = Math.round((completed / total) * 100)
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Progress
              percent={percent}
              size="small"
              style={{ width: 80, margin: 0 }}
              showInfo={false}
              strokeColor={completed === total ? '#38a169' : '#3182ce'}
            />
            <Tag
              color={completed === total ? 'green' : 'blue'}
              style={{ borderRadius: 12, fontWeight: 600, fontSize: 11 }}
            >
              {completed}/{total}
            </Tag>
          </div>
        )
      },
    },
    {
      title: 'Active Mapping Versions',
      key: 'activeHrisMappings',
      render: (_: unknown, row: CustomerOnboardingRow) => {
        const mappings = row.activeHrisMappings ?? []
        if (mappings.length === 0) {
          return '—'
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {mappings.map((m) => (
              <Tag key={m.type} color="blue" style={{ margin: 0 }}>
                {TEMPLATE_TYPE_LABELS[m.type] ?? m.type} v{m.versionNumber}
              </Tag>
            ))}
          </div>
        )
      },
    },
    {
      title: 'Eligible Rules Version',
      dataIndex: 'activeEligibleMappingVersion',
      key: 'activeEligibleMappingVersion',
      render: (value: string | null) => value ?? '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, row: CustomerOnboardingRow) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: '#003146',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation()
              navigateToCustomer(row.clientPublicKey)
            }}
          >
            <EditOutlined style={{ fontSize: 14, color: '#fff' }} />
          </div>
        </div>
      ),
    },
  ]

  return (
    <Card className={styles.allCustomersCard}>
      <div className={styles.tableHeader}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          All Customers
        </Typography.Title>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Space align="center">
            <Switch
              checked={includeTestCompanies}
              onChange={handleToggleTestCompanies}
            />
            <Typography.Text>Include test companies</Typography.Text>
          </Space>
          <Select
            showSearch
            allowClear
            placeholder="Search company"
            style={{ width: 300 }}
            optionFilterProp="label"
            value={selectedClientPublicKey}
            onChange={handleSelectClient}
            options={options.map((option) => ({
              value: option.clientPublicKey,
              label: option.name,
            }))}
          />
        </div>
      </div>
      <Table
        dataSource={data}
        columns={columns as any}
        rowKey="clientPublicKey"
        loading={isLoading}
        onRow={(row: CustomerOnboardingRow) => ({
          onClick: () => navigateToCustomer(row.clientPublicKey),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} items`,
          onChange: handlePageChange,
        }}
        locale={{ emptyText: 'No Data' }}
      />
    </Card>
  )
}
